import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTenant } from "@/context/tenant-context";
import { agentAccessApi } from "@/api/agent-access";
import { tenantUsersApi } from "@/api/tenant-users";
import { tenantGroupsApi } from "@/api/tenant-groups";
import { queryKeys } from "@/lib/query-keys";
import { roleLabel } from "@/lib/role-label";
import { Plus, Search, Trash2, UsersRound } from "lucide-react";

interface Props {
  agentId: string;
}

/**
 * Who can use this agent, as one list (2026-09-30, Kenneth). Modelled on a
 * share dialog's "People with access": everyone in one box, each person's
 * role as a tag on their row, and one "Add Member" search that finds users and
 * groups together.
 *
 * Tenant admins are listed first and cannot be removed: the server lets them
 * use every agent without a grant. They are also left out of the search,
 * because adding them would change nothing.
 */
type Row =
  | { kind: "admin"; key: string; name: string; detail: string; role: string }
  | { kind: "group"; key: string; accessId: string; name: string; detail: string }
  | { kind: "user"; key: string; accessId: string; name: string; detail: string; role: string };

type Candidate =
  | { kind: "user"; id: string; name: string; detail: string }
  | { kind: "group"; id: string; name: string; detail: string };

const isTenantAdminRole = (role: string) => role === "tenant_admin" || role === "admin";

/** "Group · 3 members · Local" */
function groupDetail(memberCount: number | undefined, source: string | null | undefined): string {
  return [
    "Group",
    memberCount !== undefined ? `${memberCount} member${memberCount !== 1 ? "s" : ""}` : null,
    source === "entra" ? "Entra" : source === "local" ? "Local" : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

export function AgentAccessTab({ agentId }: Props) {
  const { tenantId, isTenantAdmin } = useTenant();
  // Only tenant admins may add or remove people (the server refuses anyone
  // else). Agent admins see the same list read-only.
  const canEdit = isTenantAdmin;
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState("");

  const { data: entries = [], isLoading } = useQuery({
    queryKey: queryKeys.agentAccess.byAgent(agentId),
    queryFn: () => agentAccessApi.list(tenantId, agentId),
  });
  const { data: users = [] } = useQuery({
    queryKey: queryKeys.tenantUsers.byCompany(tenantId),
    queryFn: () => tenantUsersApi.list(tenantId),
  });
  const { data: groups = [] } = useQuery({
    queryKey: queryKeys.tenantGroups.byCompany(tenantId),
    queryFn: () => tenantGroupsApi.list(tenantId),
  });

  const searchInputRef = useRef<HTMLInputElement>(null);
  const closeSearch = () => {
    setAdding(false);
    setSearch("");
  };

  // Everything else that shows who can reach which agent.
  const refreshAccessViews = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.agentAccess.byAgent(agentId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantGroups.agentAccess(tenantId) }),
      queryClient.invalidateQueries({ queryKey: ["user-agent-access", tenantId] }),
      queryClient.invalidateQueries({ queryKey: ["accessible-agents", tenantId] }),
    ]);

  const grantMutation = useMutation({
    mutationFn: (data: { tenantUserId?: string; groupId?: string }) =>
      agentAccessApi.grant(tenantId, agentId, data),
    // The search stays open after adding someone, so several people can be
    // added in a row; the one just added drops out of the results.
    onSuccess: () => {
      void refreshAccessViews();
      searchInputRef.current?.focus();
    },
  });
  const revokeMutation = useMutation({
    mutationFn: (accessId: string) => agentAccessApi.revoke(tenantId, agentId, accessId),
    onSuccess: () => refreshAccessViews(),
  });

  // The search closes on a click outside it or Escape, like adding a member
  // to a group. Escape stops here so it doesn't also close the popup behind.
  const searchRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!adding) return;
    const onPointerDown = (e: PointerEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) closeSearch();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      closeSearch();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [adding]);

  const roleByUserId = useMemo(() => new Map(users.map((u) => [u.id, u.role])), [users]);
  const groupById = useMemo(() => new Map(groups.map((g) => [g.id, g])), [groups]);

  // Tenant admins first, then groups, then people added one by one.
  const rows: Row[] = useMemo(() => {
    const admins: Row[] = users
      .filter((u) => isTenantAdminRole(u.role))
      .map((u) => ({
        kind: "admin",
        key: `admin-${u.id}`,
        name: u.displayName || u.email,
        detail: u.email,
        role: u.role,
      }));
    const groupRows: Row[] = entries
      .filter((e) => e.groupId)
      .map((e) => {
        const group = groupById.get(e.groupId!);
        return {
          kind: "group",
          key: e.id,
          accessId: e.id,
          name: e.groupName ?? group?.name ?? "Group",
          detail: groupDetail(group?.memberCount, e.groupSource ?? group?.source),
        };
      });
    // A tenant admin with an old grant is already listed above.
    const userRows: Row[] = entries
      .filter((e) => e.tenantUserId && !isTenantAdminRole(roleByUserId.get(e.tenantUserId) ?? ""))
      .map((e) => ({
        kind: "user",
        key: e.id,
        accessId: e.id,
        name: e.userName || e.userEmail || "User",
        detail: e.userName ? e.userEmail ?? "" : "",
        role: roleByUserId.get(e.tenantUserId!) ?? "standard_user",
      }));
    return [...admins, ...groupRows, ...userRows];
  }, [users, entries, groupById, roleByUserId]);

  const hasOthers = rows.some((r) => r.kind !== "admin");

  // One search over users and groups. Tenant admins and anyone already on the
  // list are left out.
  const candidates: Candidate[] = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matches = (...fields: string[]) => !q || fields.some((f) => f.toLowerCase().includes(q));
    const grantedUsers = new Set(entries.map((e) => e.tenantUserId).filter(Boolean));
    const grantedGroups = new Set(entries.map((e) => e.groupId).filter(Boolean));
    const groupCandidates: Candidate[] = groups
      .filter((g) => !grantedGroups.has(g.id) && matches(g.name))
      .map((g) => ({ kind: "group", id: g.id, name: g.name, detail: groupDetail(g.memberCount, g.source) }));
    const userCandidates: Candidate[] = users
      .filter(
        (u) =>
          !isTenantAdminRole(u.role) &&
          !grantedUsers.has(u.id) &&
          matches(u.displayName ?? "", u.email),
      )
      .map((u) => ({
        kind: "user",
        id: u.id,
        name: u.displayName || u.email,
        detail: `${u.displayName ? `${u.email} · ` : ""}${roleLabel(u.role)}`,
      }));
    return [...groupCandidates, ...userCandidates];
  }, [search, entries, groups, users]);

  return (
    // p-6 matches the Config tab's inset; h-full + overflow-y-auto lets a long
    // list scroll.
    <div className="h-full space-y-3 overflow-y-auto p-6">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-medium text-text-primary">
          People who can use this agent
          {!isLoading && <span className="ml-1.5 font-normal text-text-muted">· {rows.length}</span>}
        </h3>
        {canEdit && !adding && (
          <Button size="sm" onClick={() => setAdding(true)}>
            <Plus size={14} className="mr-1.5" />
            Add Member
          </Button>
        )}
      </div>

      {adding && (
        <div ref={searchRef} className="flex flex-col gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              ref={searchInputRef}
              type="text"
              autoFocus
              placeholder="Search users and groups..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-md border border-border bg-surface py-2 pl-8 pr-3 text-sm"
            />
          </div>
          <div className="max-h-[250px] overflow-y-auto rounded-md border border-border">
            {candidates.length === 0 ? (
              <p className="py-6 text-center text-xs text-text-muted">
                {search.trim() ? "No matches." : "Everyone can already use this agent."}
              </p>
            ) : (
              candidates.map((c) => (
                <button
                  key={`${c.kind}-${c.id}`}
                  type="button"
                  disabled={grantMutation.isPending}
                  onClick={() =>
                    grantMutation.mutate(c.kind === "user" ? { tenantUserId: c.id } : { groupId: c.id })
                  }
                  className="flex w-full items-center gap-3 border-b border-border px-3 py-2.5 text-left text-sm transition-colors last:border-0 hover:bg-surface-hover"
                >
                  <RowAvatar kind={c.kind} name={c.name} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-text-primary">{c.name}</p>
                    <p className="truncate text-xs text-text-muted">{c.detail}</p>
                  </div>
                  <Plus size={16} className="shrink-0 text-accent" />
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-text-muted">Loading...</p>
      ) : (
        <div className="rounded-lg border border-border bg-surface">
          {rows.map((row) => (
            <div
              key={row.key}
              className="flex items-center justify-between gap-3 border-b border-border px-4 py-2.5 last:border-0"
            >
              <div className="flex min-w-0 items-center gap-3">
                <RowAvatar kind={row.kind} name={row.name} />
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-text-primary">{row.name}</p>
                  {row.detail && <p className="truncate text-xs text-text-muted">{row.detail}</p>}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Badge variant="secondary" className="text-xs">
                  {row.kind === "group" ? "Group" : roleLabel(row.role)}
                </Badge>
                {canEdit && row.kind !== "admin" ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0"
                    aria-label={`Remove ${row.name}`}
                    title="Remove"
                    disabled={revokeMutation.isPending}
                    onClick={() => revokeMutation.mutate(row.accessId)}
                  >
                    <Trash2 size={14} />
                  </Button>
                ) : canEdit ? (
                  // Keeps the tags lined up with the rows that have a bin.
                  <span className="w-7" aria-hidden="true" />
                ) : null}
              </div>
            </div>
          ))}
          {!hasOthers && (
            <p className="px-4 py-3 text-xs text-text-muted">
              No one else yet.{canEdit ? " Add members or groups to give them access." : ""}
            </p>
          )}
        </div>
      )}

      <p className="text-xs text-text-muted">
        {canEdit
          ? "Tenant admins can use every agent, so they can't be removed here."
          : "Only tenant admins can change who has access."}
      </p>
    </div>
  );
}

function RowAvatar({ kind, name }: { kind: "admin" | "group" | "user"; name: string }) {
  if (kind === "group") {
    return (
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent/10">
        <UsersRound size={14} className="text-accent" />
      </div>
    );
  }
  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent/10">
      <span className="text-xs font-medium text-accent">{name[0]?.toUpperCase()}</span>
    </div>
  );
}
